import Vue from "vue";
import axios from "axios";
import isNil from "lodash/isNil";
import { AccountInfo } from "../../types";
import isLength from "validator/lib/isLength";
import isEmail from "validator/lib/isEmail";
import isEmpty from "validator/lib/isEmpty";
import isMobilePhone from "validator/lib/isMobilePhone";

interface ErrorMessages {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  zip_code?: string;
  password?: string;
  i_agree?: string; // this is for the registration form
}

export const PageComponent = Vue.extend({
  props: {
    initialErrors: {
      type: Object as () => ErrorMessages,
      required: false,
      default: (): ErrorMessages => ({})
    },
    initialAccount: {
      type: Object as () => AccountInfo,
      required: false,
      default: (): AccountInfo => ({})
    }
  },

  data() {
    return {
      zipCodeVerificationCache: {} as { [key: string]: boolean },
      errors: this.initialErrors,
      account: this.initialAccount,
      submitting: false
    };
  },

  methods: {
    updateAccountInfo(
      value: string | number,
      _: ValidityState,
      { name }: { name: string }
    ) {
      this.account = { ...this.account, [name]: value };
    },

    async isZipCodeValid(zipCode: string): Promise<boolean> {
      if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
        return false;
      }

      if (Object.keys(this.zipCodeVerificationCache).includes(zipCode)) {
        return this.zipCodeVerificationCache[zipCode];
      }

      try {
        await axios.get("/ajax/geocode", {
          params: { zip_code: zipCode }
        });

        this.zipCodeVerificationCache = {
          ...this.zipCodeVerificationCache,
          [zipCode]: true
        };
      } catch {
        this.zipCodeVerificationCache = {
          ...this.zipCodeVerificationCache,
          [zipCode]: false
        };
      }

      return this.zipCodeVerificationCache[zipCode];
    },

    async validate(): Promise<boolean> {
      this.clearErrors();

      (["first_name", "last_name", "email", "phone", "zip_code"] as Array<
        keyof Omit<AccountInfo, "password_confirmation" | "password">
      >).forEach(field => {
        if (isEmpty(this.account[field] || "")) {
          this.applyError(field, "This field is required.");
        }
      });

      if (!this.hasError("email") && !isEmail(this.account.email || "")) {
        this.applyError("email", "A valid email address is required.");
      }

      if (!this.hasError("password") && !isEmpty(this.account.password || "")) {
        if (!isLength(this.account.password || "", { min: 8 })) {
          this.applyError(
            "password",
            "The password must be at least 8 characters."
          );
        } else if (
          this.account.password !== this.account.password_confirmation
        ) {
          this.applyError(
            "password",
            "The password doesn't match the confirmation"
          );
        }
      }

      if (
        !this.hasError("zip_code") &&
        !(await this.isZipCodeValid(this.account.zip_code || ""))
      ) {
        this.applyError("zip_code", "The zip code is invalid");
      }

      if (
        !this.hasError("phone") &&
        !isMobilePhone(this.account.phone || "", "en-US")
      ) {
        this.applyError("phone", "A valid phone number is required");
      }

      return !this.hasErrors();
    },

    async validateAndSubmit(event?: Event): Promise<void> {
      if (this.submitting) {
        // let the form submission go through.
        return;
      }

      event?.preventDefault();

      this.submitting = true;

      if (!(await this.validate())) {
        this.submitting = false;
        return;
      }

      (this.$refs.form as HTMLFormElement).submit();
    },

    hasError(attribute: keyof ErrorMessages): boolean {
      return !isNil(this.errors[attribute]);
    },

    /**
     * Apply an error message to the error object if one is not already set.
     *
     * @param attribute
     * @param message
     */
    applyError(attribute: keyof ErrorMessages, message: string) {
      if (this.hasError(attribute)) {
        return;
      }

      this.errors = { ...this.errors, [attribute]: message };
    },

    hasErrors(): boolean {
      return Object.values(this.errors).some(error => (error || "").length > 0);
    },

    clearErrors(): void {
      this.errors = {};
    }
  }
});
2035258209;
